import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useTeamUsage } from '@/hooks/useTeamUsage';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, AlertTriangle } from 'lucide-react';
import { PricingPlans } from './PricingPlans';
import { BillingManagement } from './BillingManagement';
import { InvoiceHistory } from './InvoiceHistory';
import { SubscriptionHistory } from './SubscriptionHistory';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

export function UsageDashboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { planLimits, usage, subscription: initialSubscription, getUsagePercentage, loading, refreshUsage } = useTeamUsage();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const [selectedBillingFrequency, setSelectedBillingFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [subscription, setSubscription] = useState(initialSubscription);
  const [isResuming, setIsResuming] = useState(false);
  // Update local subscription state when initialSubscription changes
  useEffect(() => {
    if (initialSubscription) {
      setSubscription(initialSubscription);
      // Update selected billing frequency when subscription changes
      if (initialSubscription.billing_frequency) {
        setSelectedBillingFrequency(initialSubscription.billing_frequency === 'annual' ? 'annual' : 'monthly');
      }
    } else {
      setSubscription(null);
    }
  }, [initialSubscription]);



  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading usage data...</CardTitle>
        </CardHeader>
      </Card>
    );
  }


  // Only show "No Billing Information" if we're not loading and truly have no data
  // Allow rendering with just planLimits and usage even if subscription is null (for canceled subscriptions)
  if (!planLimits || !usage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Billing Information</CardTitle>
          <CardDescription>
            You don't have an active subscription yet. If you just completed checkout, try syncing from Polar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            If you just completed checkout, your subscription will appear shortly. We're automatically syncing your subscription data from Polar.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'trialing': return 'secondary';
      case 'past_due': return 'destructive';
      case 'canceled': return 'outline';
      default: return 'outline';
    }
  };

  // Get billing frequency from subscription data (stored from Polar)
  // Fallback to calculating from period length if not available
  const getBillingFrequency = () => {
    // First try to use stored billing_frequency from Polar
    if ((subscription as any).billing_frequency) {
      return (subscription as any).billing_frequency === 'annual' ? 'annual' : 'monthly';
    }
    
    // Fallback: determine from period length (not reliable during trial)
    if (!subscription.current_period_start || !subscription.current_period_end) {
      return 'monthly';
    }
    const start = new Date(subscription.current_period_start);
    const end = new Date(subscription.current_period_end);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'monthly';
    }
    const daysDiff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    // If period is more than 60 days, it's annual (typically 365 days)
    return daysDiff > 60 ? 'annual' : 'monthly';
  };

  // Check if billing period dates are valid and different
  const getBillingPeriodDisplay = () => {
    if (!subscription.current_period_start || !subscription.current_period_end) {
      return 'Billing period information unavailable';
    }

    const start = new Date(subscription.current_period_start);
    const end = new Date(subscription.current_period_end);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Billing period information unavailable';
    }

    const timeDiff = Math.abs(end.getTime() - start.getTime());
    if (timeDiff < 60000) {
      return 'Billing period information unavailable';
    }

    return `${formatDate(subscription.current_period_start)} - ${formatDate(subscription.current_period_end)}`;
  };

  const billingFrequency = getBillingFrequency();

  const handleResumeSubscription = async () => {
    setIsResuming(true);
    try {
      // Prepare headers for impersonation if active
      const headers: Record<string, string> = {};
      if (isImpersonating && impersonatedUser?.id) {
        headers['X-Impersonated-User-Id'] = impersonatedUser.id;
      }

      const { data, error } = await supabase.functions.invoke('resume-subscription', {
        headers,
      });

      if (error) throw error;

      // Invalidate query cache to force fresh data
      queryClient.invalidateQueries({ queryKey: ['team-usage'] });

      // Refresh subscription data
      await refreshUsage();

      toast({
        title: subscription?.pending_downgrade_tier ? 'Downgrade Canceled' : 'Subscription Resumed',
        description: data?.message || (subscription?.pending_downgrade_tier 
          ? 'Your pending downgrade has been canceled.'
          : 'Your subscription has been successfully resumed.'),
      });
    } catch (error) {
      logger.error('Error resuming subscription:', error);
      toast({
        title: 'Failed to Resume Subscription',
        description: error instanceof Error ? error.message : 'Could not resume your subscription',
        variant: 'destructive',
      });
    } finally {
      setIsResuming(false);
    }
  };


  return (
    <div className="space-y-8">
      {/* Pending Downgrade/Cancellation Alert - At top */}
      {/* Show alert if: pending_downgrade_date exists (indicates scheduled cancellation/downgrade) */}
      {subscription && subscription.pending_downgrade_date && (
        <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-800 dark:text-amber-200">Subscription Change Scheduled</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            {subscription.pending_downgrade_tier ? (
              <>
                Your subscription will be downgraded to the <span className="font-semibold capitalize">{subscription.pending_downgrade_tier}</span> plan at the end of your current billing cycle on{' '}
                <span className="font-semibold">{formatDate(subscription.pending_downgrade_date)}</span>.
              </>
            ) : (
              <>
                Your subscription will be canceled at the end of your current billing cycle on{' '}
                <span className="font-semibold">{formatDate(subscription.pending_downgrade_date)}</span>.
              </>
            )}
            {' '}You'll continue to have access to your current plan features until then.
          </AlertDescription>
          <div className="mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResumeSubscription}
              disabled={isResuming}
              className="border-amber-600 text-amber-700 hover:bg-amber-100 hover:text-amber-800 hover:border-amber-700 dark:border-amber-500 dark:text-amber-400 dark:hover:bg-amber-950/60 dark:hover:text-amber-300 dark:hover:border-amber-400 transition-colors font-medium"
            >
              {isResuming 
                ? 'Processing...' 
                : subscription.pending_downgrade_tier 
                  ? 'Cancel Downgrade' 
                  : 'Resume Subscription'}
            </Button>
          </div>
        </Alert>
      )}

      {/* Usage Metrics - Moved to top */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* North Reports */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">North Reports</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {usage.norths_created}
              {planLimits.max_active_norths !== null && (
                <span className={`text-sm font-normal ${usage.norths_created > planLimits.max_active_norths ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {' '}/ {planLimits.max_active_norths}
                </span>
              )}
            </div>
            {planLimits.max_active_norths !== null ? (
              <>
                <Progress
                  value={Math.min(getUsagePercentage('norths'), 100)}
                  className={`mt-2 ${usage.norths_created > planLimits.max_active_norths ? '[&>div]:bg-red-500' : ''}`}
                />
                <p className={`text-xs mt-1 ${usage.norths_created > planLimits.max_active_norths ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {usage.norths_created > planLimits.max_active_norths
                    ? `${usage.norths_created - planLimits.max_active_norths} over limit`
                    : `${Math.round(getUsagePercentage('norths'))}% of plan limit used`}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">Unlimited</p>
            )}
          </CardContent>
        </Card>

        {/* CMAs This Month */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CMAs This Month</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {usage.cmas_this_month}
              {planLimits.max_cmas_per_month !== null && (
                <span className={`text-sm font-normal ${usage.cmas_this_month > planLimits.max_cmas_per_month ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {' '}/ {planLimits.max_cmas_per_month}
                </span>
              )}
            </div>
            {planLimits.max_cmas_per_month !== null ? (
              <>
                <Progress
                  value={Math.min((usage.cmas_this_month / planLimits.max_cmas_per_month) * 100, 100)}
                  className={`mt-2 ${usage.cmas_this_month > planLimits.max_cmas_per_month ? '[&>div]:bg-red-500' : ''}`}
                />
                <p className={`text-xs mt-1 ${usage.cmas_this_month > planLimits.max_cmas_per_month ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {usage.cmas_this_month > planLimits.max_cmas_per_month
                    ? `${usage.cmas_this_month - planLimits.max_cmas_per_month} over limit`
                    : `${Math.round((usage.cmas_this_month / planLimits.max_cmas_per_month) * 100)}% of monthly limit`}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">Unlimited</p>
            )}
          </CardContent>
        </Card>

        {/* Buyer Reports This Month */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Buyer Reports This Month</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {planLimits.max_property_reports_per_month === 0 ? (
              <div>
                <p className="text-2xl font-bold text-muted-foreground">—</p>
                <p className="text-xs text-muted-foreground mt-2">Not included on this plan</p>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {usage.property_reports_this_month}
                  {planLimits.max_property_reports_per_month !== null && (
                    <span className={`text-sm font-normal ${usage.property_reports_this_month > planLimits.max_property_reports_per_month ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {' '}/ {planLimits.max_property_reports_per_month}
                    </span>
                  )}
                </div>
                {planLimits.max_property_reports_per_month !== null ? (
                  <>
                    <Progress
                      value={Math.min((usage.property_reports_this_month / planLimits.max_property_reports_per_month) * 100, 100)}
                      className={`mt-2 ${usage.property_reports_this_month > planLimits.max_property_reports_per_month ? '[&>div]:bg-red-500' : ''}`}
                    />
                    <p className={`text-xs mt-1 ${usage.property_reports_this_month > planLimits.max_property_reports_per_month ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                      {usage.property_reports_this_month > planLimits.max_property_reports_per_month
                        ? `${usage.property_reports_this_month - planLimits.max_property_reports_per_month} over limit`
                        : `${Math.round((usage.property_reports_this_month / planLimits.max_property_reports_per_month) * 100)}% of monthly limit`}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground mt-2">Unlimited</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Over Limit Warning */}
      {planLimits.max_active_norths !== null && usage.norths_created > planLimits.max_active_norths && (
        <Alert className="border-red-500/50 bg-red-50 dark:bg-red-950/20">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <AlertTitle className="text-red-800 dark:text-red-200">Over Plan Limit</AlertTitle>
          <AlertDescription className="text-red-700 dark:text-red-300">
            You have <span className="font-semibold">{usage.norths_created} active North Reports</span> but your current plan only allows{' '}
            <span className="font-semibold">{planLimits.max_active_norths}</span>.
            Your existing reports will remain active, but you won't be able to publish new ones until you're under your limit.
            Consider upgrading your plan or unpublishing {usage.norths_created - planLimits.max_active_norths} report{usage.norths_created - planLimits.max_active_norths > 1 ? 's' : ''}.
          </AlertDescription>
        </Alert>
      )}

      <Separator />

      {/* Trial Period Alert - Show if trialing */}
      {subscription?.status === 'trialing' && subscription.trial_ends_at && (
        <Alert className="border-blue-500/50 bg-blue-50 dark:bg-blue-950/20">
          <AlertTriangle className="h-4 w-4 text-blue-500" />
          <AlertTitle className="text-blue-800 dark:text-blue-200">Trial Period Active</AlertTitle>
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            You're currently on a trial period. Upgrading will be available after your trial ends on{' '}
            <span className="font-semibold">{formatDate(subscription.trial_ends_at)}</span>.
            You can continue using your current plan features until then.
          </AlertDescription>
        </Alert>
      )}

      {/* Available Plans */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Upgrade Plan</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Choose the plan that best fits your needs
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedBillingFrequency === 'monthly' ? (
              <span className="text-sm text-muted-foreground">Pay annually and save</span>
            ) : (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-300">
                <Badge className="bg-green-600 hover:bg-green-600 text-white px-3 py-1">
                  1 Month Free
                </Badge>
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Save up to $249/year</span>
              </div>
            )}
            <div className="relative bg-primary/10 p-1 rounded-full inline-flex border border-primary/30">
              <button
                onClick={() => setSelectedBillingFrequency('monthly')}
                className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  selectedBillingFrequency === 'monthly'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-primary/70 hover:text-primary'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedBillingFrequency('annual')}
                className={`relative px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  selectedBillingFrequency === 'annual'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-primary/70 hover:text-primary'
                }`}
              >
                Annual
              </button>
            </div>
          </div>
        </div>
        <PricingPlans
          currentPlan={planLimits?.plan_tier || 'starter'}
          hideToggle={true}
          externalBillingFrequency={selectedBillingFrequency}
          onBillingFrequencyChange={setSelectedBillingFrequency}
          currentBillingFrequency={billingFrequency}
          hideFooterText={true}
          subscriptionStatus={subscription?.status}
        />
      </div>

      <Separator />

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl capitalize">
                {planLimits.plan_tier === 'pro' ? 'Growth' : planLimits.plan_tier === 'premium' ? 'Scale' : planLimits.plan_tier === 'growth' ? 'Growth' : planLimits.plan_tier === 'scale' ? 'Scale' : planLimits.plan_tier} Plan
                <Badge variant="secondary" className="ml-3 capitalize text-sm font-normal">
                  {billingFrequency}
                </Badge>
              </CardTitle>
              <CardDescription>
                Current billing period: {getBillingPeriodDisplay()}
              </CardDescription>
            </div>
            <Badge variant={getStatusColor(subscription.status)} className="capitalize">
              {subscription.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {subscription.trial_ends_at && new Date(subscription.trial_ends_at) > new Date() && (
            <div className="text-sm text-muted-foreground">
              Trial ends {formatDate(subscription.trial_ends_at)}
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Invoice History */}
      <InvoiceHistory />

      <Separator />

      {/* Subscription History */}
      <SubscriptionHistory />

      <Separator />

      {/* Billing Management - Moved to bottom */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Billing Management</CardTitle>
        </CardHeader>
        <CardContent>
          <BillingManagement 
            subscription={subscription} 
            onSubscriptionChange={async () => {
              // Invalidate query cache to force fresh data
              queryClient.invalidateQueries({ queryKey: ['team-usage'] });
              
              // Refresh usage data
              await refreshUsage();
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}